import Duck from "@/components/common/3D/Duck";
import { AnimationStateKeys, useDuck } from "@/provider/DuckContext";
import { useDeviceDetector } from "@/utils/useDeviceDetector";
import { BreakPointProps, useResponsive3D } from "@/utils/useResponsive3D";
import { useGSAP } from "@gsap/react";
import { Float, PerspectiveCamera } from "@react-three/drei";
import { Canvas } from "@react-three/fiber";
import gsap from "gsap/all";
import { useRef, useState } from "react";
import { degToRad } from "three/src/math/MathUtils";

const JoinUsDuck = ({ endTrigger }) => {
  const cameraRef = useRef(null);
  const canvasRef = useRef(null);
  const duckRef = useRef(null);
  const timelineRef = useRef(null);

  const [isReady, setReady] = useState(false);

  const { isMobile } = useDeviceDetector();

  const { handleDuckAnimation } = useDuck();

  const breakPoints: BreakPointProps[] = [
    {
      breakpoint: [300, 1000],
      props: {
        position: [0.1, 1.75, 10],
        duckRotation: 0,
        duckScale: 2,
      },
    },
    {
      breakpoint: [1001, 1200],
      props: {
        position: [-2, 0.5, 10],
        duckRotation: -45,
        duckScale: 3,
      },
    },
    {
      breakpoint: [1201, 1600],
      props: {
        position: [-2.25, 1.5, 10],
        duckRotation: -45,
        duckScale: 3.5,
      },
    },
    {
      breakpoint: [1601, 1900],
      props: {
        position: [-3, 1.5, 10],
        duckRotation: -45,
        duckScale: 4,
      },
    },
    {
      breakpoint: [1901, 2500],
      props: {
        position: [-3, 1.5, 10],
        duckRotation: -45,
        duckScale: 4,
      },
    },
  ];

  const { breakProps } = useResponsive3D({ breakPoints: breakPoints });

  useGSAP(
    () => {
      if (!isReady) return;

      if (timelineRef.current) timelineRef.current.kill();

      if (!isMobile) {
        timelineRef.current = gsap.timeline({
          scrollTrigger: {
            trigger: canvasRef.current,
            start: "top top",
            endTrigger: endTrigger.current,
            end: "bottom-=100px bottom",
            pin: true,
            pinSpacing: false,
            scrub: true,
            invalidateOnRefresh: true,
          },
        });

        timelineRef.current.to(cameraRef.current?.position, {
          x: breakProps?.position[0],
          ease: "power4.inOut",
          onComplete: () => {
            handleDuckAnimation(AnimationStateKeys.JUMP);
          },
        });

        timelineRef.current.to(duckRef.current.rotation, {
          delay: 0.5,
          y: degToRad(360),
          ease: "power4.inOut",
          onComplete: () => {
            handleDuckAnimation(AnimationStateKeys.BULLY);
          },
        });
        timelineRef.current.to(
          cameraRef.current?.position,
          {
            x: 0,
            z: 12,
            ease: "power4.inOut",
          },
          "<"
        );
      } else {
        timelineRef.current = gsap.timeline({
          scrollTrigger: {
            trigger: canvasRef.current,
            start: "top top",
            endTrigger: endTrigger.current,
            end: "bottom-=100px bottom",
            pin: true,
            pinSpacing: false,
            scrub: true,
            invalidateOnRefresh: true,
          },
        });
        timelineRef.current.fromTo(
          canvasRef.current,
          { opacity: 1 },
          {
            opacity: 0,
            ease: "power4.inOut",
          }
        );
        timelineRef.current.fromTo(
          cameraRef.current.position,
          { x: 0.1 },
          {
            x: 10,
            ease: "power4.inOut",
          },
          "<"
        );
        timelineRef.current.fromTo(
          canvasRef.current,
          { opacity: 0 },
          {
            opacity: 1,
            ease: "power4.inOut",
            onComplete: () => handleDuckAnimation(AnimationStateKeys.SIDE),
          }
        );
        timelineRef.current.fromTo(
          cameraRef.current.position,
          { x: -10 },
          {
            x: 0.1,
            ease: "power4.inOut",
          },
          "<"
        );
      }
    },
    {
      dependencies: [isReady, isMobile, breakProps],
      revertOnUpdate: true,
    }
  );

  return (
    <div
      ref={canvasRef}
      style={{
        width: "100vw",
        height: "100vh",
        position: "absolute",
        top: 0,
        left: 0,
        zIndex: 26,
        pointerEvents: "none",
        overflow: "hidden",
      }}
    >
      <Canvas
        gl={{
          stencil: false,
          autoClear: true,
          alpha: true,
          antialias: true,
          precision: "mediump",
          powerPreference: "high-performance",
        }}
        style={{
          width: "100%",
          height: "100%",
        }}
        onCreated={() => setReady(true)}
        frameloop="always"
      >
        <PerspectiveCamera
          ref={cameraRef}
          position={[
            breakProps?.position[0],
            breakProps?.position[1],
            breakProps?.position[2],
          ]}
          fov={30}
          makeDefault
        />
        <group
          ref={duckRef}
          position={[0, 0, 0]}
          rotation={[0, degToRad(breakProps?.duckRotation), 0]}
        >
          <Float speed={2} floatIntensity={0.5}>
            <Duck
              scale={breakProps?.duckScale}
              cursorAnimations
              cursorAngleLimit={15}
            />
          </Float>
        </group>
      </Canvas>
    </div>
  );
};

export default JoinUsDuck;

JoinUsDuck.displayName = "JoinUsDuck";
